import { useMutation } from '@apollo/client';
import React from 'react';
import styled from 'styled-components';

import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';
import { useScrollToElement } from '../../../../utils/hooks/useScrollToElement';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import { routeConfig } from '../../../../__config__/routes';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Container } from '../../../layout/Container';
import { Section } from '../../../layout/Section';
import Arrow from '../../../ui/arrow/Index';
import { Button } from '../../../ui/button/Button';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import ButtonGroupGrid from '../../../ui/button/ButtonGroupGrid';
import { Form, FormRow, FormWrapper } from '../../../ui/form/Form';
import { FormError } from '../../../ui/form/FormError';
import { Input } from '../../../ui/form/Input';
import { Hero, HeroContent } from '../../../ui/hero/Index';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { ArticleHighlight } from './ArticleHighlight';
import { Thanks } from '../../../layout/Thanks';
import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';
import { Seo } from '../../../../utils/seo/Index';

const KvadratStyle = styled.div`
  .secondIntro {
    margin-top: 2em;
    font-family: 'Proxima Nova';
  }

  .no-margin {
    margin-top: 0;
  }
  @media all and (max-width: 600px) {
    .firstIntro {
      margin-top: 2em;
    }
  }

  @media all and (min-width: 600px) {
    .secondIntro {
      margin-top: 4em;
      font-family: 'Proxima Nova';
    }

    .no-margin {
      margin-top: 0;
    }
    .bottom {
      margin-top: -9em;
    }
    .firstIntro {
      margin-top: -3em;
    }
  }
`;
const Wrapper = styled.div``;

const ArrowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-top: 2em;
`;
const BigSpace = styled.div`
  position: relative;
  height: 380px;

  @media all and (max-width: 600px) {
    height: unset;
  }
`;

const HighlightContainer = styled.div``;

interface IKvadrat {}

const Kvadrat: React.FC<IKvadrat> = () => {
  const formRef = React.useRef(null);
  const infoRef = React.useRef(null);
  /*   const infoRef = React.useRef<HTMLDivElement>(null); */
  const [posted, setPosted] = React.useState(false);
  const { scrollTo } = useScrollToElement();
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { submitWithDigger } = useDigtectiveContext();

  const [lead, { error, loading }] = useMutation(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.valuation?.success) {
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Postnummer *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <KvadratStyle>
      <BigSpace />
      <Container className="firstIntro">
        <Wrapper>
          <Seo
            title="PrivatMegleren - Alt på ett sted med Kvadrat"
            description="Kvadrat gir deg overblikket og kontrollen fra vi setter i gang, og er med deg hele veien til solgt."
            image=""
            url=""
          />
          <Section
            className="form-section"
            style={{
              paddingBottom: '0'
            }}
          >
            <Heading style={{ textAlign: 'center ', margin: '0' }} tag="h1">
              Velkommen til PrivatMegleren Kvadrat
            </Heading>

            {/*             <Paragraph
              center
              style={{
                fontFamily: 'Proxima Nova',
                fontSize: '20px'
              }}
            ></Paragraph> */}
          </Section>
          <ButtonGroupGrid style={{ paddingTop: '3em' }}>
            <Button
              className="blikontaktet"
              type="primary"
              colorTheme="gold"
              fullWidth={true}
              center={true}
              onClick={() => {
                if (typeof scrollTo === 'function') {
                  scrollTo({
                    ref: formRef,
                    distanceTop: 100,
                    behaviour: 'instant'
                  });
                }
              }}
            >
              Bli kontaktet
            </Button>
          </ButtonGroupGrid>
        </Wrapper>
        <Paragraph className="secondIntro" center>
          PrivatMegleren Kvadrat gir deg overblikket og kontrollen fra vi setter
          i gang, og er din personlige assistent hele veien til solgt. Tjenesten
          gir deg informasjon i sanntid om interessenter, antall påmeldte til
          visning, budgivere, og du kan følge budrunden direkte. Gjennom Kvadrat
          vil du se hvordan vi jobber og hvorfor vår skreddersøm sikrer at vi
          oppnår det beste resultatet på alle eiendommene vi omsetter.
        </Paragraph>
        <EmbedContainer style={{ marginTop: '4em' }}>
          <iframe
            className="VimeoOuterContainer"
            src="https://player.vimeo.com/video/841215727"
            /*  src="https://player.vimeo.com/video/799046455?amp;loop=0&background=1&autoplay=0&muted=1&controls=0" */
            frameBorder="0"
            width="100%"
            height="100%"
          ></iframe>
        </EmbedContainer>
        <Paragraph className="secondIntro no-margin" center>
          Kvadrat er skapt for å gi deg den aller beste opplevelsen når du skal
          selge noe av det mest verdifulle du har.
        </Paragraph>
        <ArrowWrapper>
          <Arrow
            onClick={() => {
              if (typeof scrollTo === 'function') {
                scrollTo({
                  ref: infoRef,
                  distanceTop: 100,
                  behaviour: 'smooth'
                });
              }
            }}
          />
        </ArrowWrapper>
      </Container>

      <HighlightContainer ref={infoRef}>
        <ArticleHighlight
          style={{ padding: '5em 0 0', fontFamily: 'Proxima Nova' }}
          className="reverse"
          title="Hva du kan forvente av våre tjenester og leveranse"
          text={
            'Vi ønsker at våre kunder skal ha høye forventninger til oss. Ni av ti kunder velger oss fordi vi er anbefalt av tidligere kunder. Vi håper og tror dette er en bekreftelse på at våre meglere leverer en opplevelse og resultater som overgår de fleste forventninger til en eiendomsmegler.'
          }
          image={
            'https://cdn.reeltime.no/pm_assets/kampanjer/kvadrat/Kvadrat-statistic.jpg'
          }
          imageOverlay={''}
        />
        <ArticleHighlight
          style={{
            paddingBottom: '0',

            zIndex: '999',
            display: 'block',
            fontFamily: 'Proxima Nova'
          }}
          className="alignitems middle"
          title="Kundene ivaretas gjennom hele salgsprosessen"
          text={
            'I Kvadrat følger du salget ditt – steg for steg. Her viser vi hvordan vi skreddersyr leveransen som gjøres for eiendommene vi omsetter. Du ser alt megleren gjør og oppgavene du selv må gjøre, som for eksempel alle dokumenter til signering. Her får du også se bilder og prospektet ditt, som du enkelt godkjenner før publisering.'
          }
          image={
            'https://cdn.reeltime.no/pm_assets/kampanjer/kvadrat/Kvadrat-sales.jpg'
          }
          imageOverlay={''}
        />
        <ArticleHighlight
          style={{ paddingTop: '0', fontFamily: 'Proxima Nova' }}
          className="alignitems reverse bottom"
          title="Kvadrat holder deg løpende informert"
          text={
            'Du kan se statistikk over interessenter og antall påmeldte til visning. Deretter kan du følge med på interessen for eiendommen din, hvor mange som kommer på visning, og til slutt budrunden, som du kan følge live. Hvor involvert du ønsker å være er helt opp til deg, sjekk inn så ofte du vil og velg selv om du ønsker å bli varslet når det det skjer noe nytt.'
          }
          image={
            'https://cdn.reeltime.no/pm_assets/kampanjer/kvadrat/Kvadrat-mobilklikkfinn.jpg'
          }
          imageOverlay={''}
        />
      </HighlightContainer>
      <Container>
        <FormWrapper ref={formRef} style={fadeOut}>
          <Section
            className="form-section"
            style={{
              paddingBottom: '10px',
              paddingTop: '0',
              fontFamily: 'Proxima Nova'
            }}
          >
            <Heading tag="h2" center={true}>
              Bli kontaktet
            </Heading>
            <Paragraph center={true} className="form-text">
              Vi håper vi får muligheten til å introdusere deg for
              PrivatMegleren Kvadrat og bidra til å gi deg og din eiendom den
              aller beste salget.
            </Paragraph>
            <Paragraph center={true} className="form-text">
              Fyll ut skjemaet og en av våre meglere i ditt nærområde kontakt.
            </Paragraph>
          </Section>

          <Form
            onSubmit={handleSubmit}
            noValidate
            style={{ paddingBottom: '50px' }}
          >
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item, index) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item?.type}
                    name={item?.name}
                    label={item?.label}
                    labelAfter={item?.labelAfter}
                    placeholder={item?.placeholder}
                    value={item?.value}
                    onChange={handleChange}
                    error={item?.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Kontakt meg
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>

      <BackgroundLoad
        opacity={0.8}
        url="https://cdn.reeltime.no/pm_assets/kampanjer/kvadrat/Kvadrat-header.jpg"
        showOnMobile
      />
    </KvadratStyle>
  );
};

export default Kvadrat;

const EmbedContainer = styled.div`
  position: relative;
  padding: 58.07% 0 0 0;
  .VimeoOuterContainer {
    position: absolute;
    top: 0;
    left: 0;
    padding-bottom: 4em;
  }

  @media all and (max-width: 1200px) {
    padding: 73.07% 0 0 0;
    margin-top: 0px;
    .VimeoOuterContainer {
      padding-bottom: 4em;
    }
  }
`;
